import React, { ButtonHTMLAttributes, forwardRef } from 'react';
import cx from 'classnames';
import Tip from '../Tip/Tip';
import Icon, { IconName } from '../icons/Icon';
import './Button.css';

/**
 * Jerarquía de acciones:
 * - primary: una por pantalla (Guardar, Buscar).
 * - secondary: Cancelar, Exportar.
 * - tertiary: texto subrayado (Limpiar formulario).
 * - danger: destructiva, siempre con ConfirmDialog; nunca primaria.
 * - ghost: acción de icono dentro de tablas y listas.
 */
export type ButtonKind = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'ghost';

/** Alto: 40 en formularios, 36 en barras de herramientas, 32 en paginación. */
export type ButtonSize = 'form' | 'toolbar' | 'pager';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: ButtonKind;
  size?: ButtonSize;
  icon?: IconName;
  /** Solo icono: exige `aria-label` en español. */
  iconOnly?: boolean;
  busy?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { kind = 'secondary', size = 'form', icon, iconOnly, busy, className, children, type = 'button', disabled, ...rest },
    ref
  ) => {
    const boton = (
    <button
      ref={ref}
      type={type}
      className={cx(
        'ui-button',
        `ui-button--${kind}`,
        `ui-button--${size}`,
        iconOnly && 'ui-button--icon',
        busy && 'ui-button--busy',
        className
      )}
      aria-busy={busy || undefined}
      /* Mientras espera respuesta no admite un segundo clic. */
      disabled={disabled || busy}
      {...rest}
    >
      {/*
       * Mientras carga, el icono propio cede el sitio al de recarga, que gira:
       * `aria-busy` solo anuncia la espera al lector de pantalla, no se ve.
       */}
      {busy ? (
        <Icon name="refresh" size={size === 'pager' ? 15 : 16} />
      ) : (
        icon && <Icon name={icon} size={size === 'pager' ? 15 : 16} />
      )}
      {!iconOnly && children}
    </button>
    );
    /*
     * Solo icono: su nombre sale en el globo de la aplicación y no en el
     * `title` del navegador, que tarda en salir y no admite estilo. Con texto
     * no hace falta: el botón ya dice lo que hace.
     */
    const nombre = rest['aria-label'];
    if (!iconOnly || !nombre) {
      return boton;
    }
    return (
      <Tip id={`btn-${nombre}`} label={nombre} tipo="nombre">
        {boton}
      </Tip>
    );
  }
);

Button.displayName = 'Button';

export default Button;
